import React, { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import {
  ProcurementSellerRegistrationStatus,
  RegisterProcurementSupplierDto,
} from "@green0meter/api-client";
import { useNavigate } from "react-router-dom";

import RegisterProcurementSupplierForm, {
  RegisterProcurementSupplierFormValues,
} from "./RegisterProcurementSupplierForm";

import { useLogin, useRegisterProcurementSupplier } from "@api";
import { useRegisterSupplierStore } from "@store/registerSupplierStore";
import { ConfirmationModal } from "@components/common";
import { Error } from "@helpers";

type RegisterProcurementSupplierFormContainer = {
  handleGoBack: () => void;
};

const RegisterProcurementSupplierFormContainer: FC<
  RegisterProcurementSupplierFormContainer
> = ({ handleGoBack }) => {
  const { t } = useTranslation();
  const { validatedData, userData, setUserData } = useRegisterSupplierStore();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const schema = yup
    .object({
      firstName: yup.string().trim().required(t("validation.firstName")),
      lastName: yup.string().trim().required(t("validation.lastName")),
      email: yup
        .string()
        .trim()
        .email(t("validation.email"))
        .max(255)
        .required(t("validation.required")),
      phone: yup
        .string()
        .trim()
        .transform((value) => value.replace(/\s+/g, ""))
        .required(t("validation.phone")),
      password: yup.string().max(128).required(t("validation.required")),
      repeatPassword: yup
        .string()
        .oneOf([yup.ref("password")], t("validation.errors.passwords_do_not_match"))
        .required(t("validation.repeatPassword")),
      acceptTerms: yup
        .boolean()
        .required(t("validation.accept_terms"))
        .oneOf([true], t("validation.accept_terms")),
    })
    .required();

  const { control, handleSubmit, watch, setError } =
    useForm<RegisterProcurementSupplierFormValues>({
      defaultValues: {
        firstName: userData?.firstName || "",
        lastName: userData?.lastName || "",
        email: userData?.email || "",
        phone: userData?.phone || "",
        password: userData?.password || "",
        repeatPassword: userData?.repeatPassword || "",
        acceptTerms: userData?.acceptTerms || false,
      },
      resolver: yupResolver(schema),
      mode: "onTouched",
    });

  const { login } = useLogin();

  const { registerSupplierAsync, registerSupplierData, isSuccessRegisterSupplier } =
    useRegisterProcurementSupplier(setError);

  const onSubmit = async (
    values: RegisterProcurementSupplierFormValues,
  ): Promise<void> => {
    try {
      setUserData(values);
      setIsSubmitting(true);

      const payload: RegisterProcurementSupplierDto = {
        ...values,
        phoneNumber: values.phone,
        activeCompanyId: validatedData!.companyId,
        companyName: validatedData!.companyName,
        country: validatedData!.country,
        vat: validatedData!.vat,
      };

      await registerSupplierAsync(payload);
    } catch (error) {
      Error(error);
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = (): void => {
    setConfirmationModalOpen(false);
  };

  // TODO: add error handling from BE to propagate the error into the form - then refactor this
  useEffect(() => {
    if (isSuccessRegisterSupplier && registerSupplierData?.status !== undefined) {
      switch (registerSupplierData.status) {
        case ProcurementSellerRegistrationStatus.SuccessfullyRegistered:
          login(
            {
              username: watch("email"),
              password: watch("password"),
            },
            {
              onSuccess: () => {
                setIsSubmitting(false);
                navigate(`/suppliers-hub/${registerSupplierData.procurementId}`);
              },
              onError: () => {
                setIsSubmitting(false);
              },
            },
          );
          break;
        default:
          setConfirmationModalOpen(true);
      }
      setIsSubmitting(false);
    }
  }, [isSuccessRegisterSupplier, registerSupplierData]);

  return (
    <>
      <RegisterProcurementSupplierForm
        onSubmit={handleSubmit(onSubmit)}
        goToPreviousStep={handleGoBack}
        control={control}
        watch={watch}
        isSubmitting={isSubmitting}
      />
      <ConfirmationModal
        open={confirmationModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleCloseModal}
        title={t("csob.supplier.hub.registration.confirmationModal.title")}
        description={t(
          "csob.supplier.hub.registration.confirmationModal.description",
        )}
        confirmText={t("common.close")}
        noCancel
      />
    </>
  );
};

export default RegisterProcurementSupplierFormContainer;
